import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import {
  Loader2, Users, ReceiptIndianRupee, GraduationCap, TrendingUp,
  Sparkles, CheckCircle2, XCircle, ExternalLink, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const who = await api.get("/admin/whoami");
        if (!who.data.is_admin) {
          toast.error("You don't have admin access");
          navigate("/dashboard", { replace: true });
          return;
        }
        setAllowed(true);
      } catch {
        navigate("/", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const [s, u, p] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/payments"),
        ]);
        setStats(s.data);
        setUsers(u.data.items);
        setPayments(p.data.items);
      } catch {
        toast.error("Could not load admin data");
      } finally {
        setLoading(false);
      }
    })();
  }, [allowed]);

  if (checking || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F0]" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (!allowed) return null;

  const fmtDate = (s) => {
    if (!s) return "-";
    try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };
  const fmtINR = (n) => "INR " + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin console
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
              CareerWisdom &mdash; Ops
            </h1>
            <p className="mt-3 text-sm text-[#5C5C55]">
              Signed in as <span className="font-mono">{user.email}</span>
            </p>
          </div>
          <Link to="/dashboard" data-testid="admin-back" className="btn-secondary">
            Back to app
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#E5E5E0]">
          {[
            ["overview", "Overview"],
            ["users", `Users (${users.length})`],
            ["payments", `Payments (${payments.length})`],
            ["diag", "Diagnostics"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#5C5C55] hover:text-[#0A0A0A]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#5C5C55]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading data…
          </div>
        ) : tab === "overview" ? (
          <Overview stats={stats} fmtINR={fmtINR} />
        ) : tab === "users" ? (
          <UsersTable users={users} fmtDate={fmtDate} />
        ) : tab === "payments" ? (
          <PaymentsTable payments={payments} fmtDate={fmtDate} fmtINR={fmtINR} />
        ) : (
          <Diagnostics
            data={diagnostics}
            loading={diagLoading}
            onRun={async () => {
              setDiagLoading(true);
              try {
                const r = await api.get("/admin/diagnostics");
                setDiagnostics(r.data);
              } catch (e) {
                toast.error("Diagnostics call failed: " + (e?.response?.data?.detail || e.message));
              } finally {
                setDiagLoading(false);
              }
            }}
          />
        )}
      </div>
    </main>
  );
}

function Overview({ stats, fmtINR }) {
  if (!stats) return null;
  const cards = [
    { label: "Total users", value: stats.total_users, Icon: Users, accent: "#002FA7" },
    { label: "Users with quiz", value: stats.users_with_quiz, Icon: GraduationCap, accent: "#002FA7" },
    { label: "Quizzes taken", value: stats.total_quizzes_taken, Icon: Sparkles, accent: "#0A0A0A" },
    { label: "Saved careers", value: stats.saved_careers, Icon: Sparkles, accent: "#0A0A0A" },
    { label: "Courses tracked", value: stats.courses_tracked, Icon: GraduationCap, accent: "#0A0A0A" },
    { label: "Paid payments", value: stats.total_paid, Icon: ReceiptIndianRupee, accent: "#166534" },
    { label: "Unique paying users", value: stats.unique_paid_users, Icon: Users, accent: "#166534" },
    { label: "Conversion", value: `${stats.conversion_pct}%`, Icon: TrendingUp, accent: "#166534" },
  ];
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="border border-[#E5E5E0] bg-white p-5 rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <c.Icon className="w-4 h-4 shrink-0" style={{ color: c.accent }} strokeWidth={1.6} />
            </div>
            <div className="overline">{c.label}</div>
            <div className="font-heading text-3xl font-black tracking-tighter mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="border border-[#E5E5E0] bg-white p-6 rounded-sm">
        <div className="overline mb-3">Revenue</div>
        <div className="grid sm:grid-cols-3 gap-6">
          <div>
            <div className="text-xs text-[#5C5C55] mb-1">Total revenue</div>
            <div className="font-heading text-3xl font-black tracking-tighter text-[#166534]">
              {fmtINR(stats.revenue_total_inr)}
            </div>
          </div>
          <div>
            <div className="text-xs text-[#5C5C55] mb-1">Live (real money)</div>
            <div className="font-heading text-2xl font-black tracking-tighter text-[#166534]">
              {fmtINR(stats.revenue_live_inr)}
            </div>
          </div>
          <div>
            <div className="text-xs text-[#5C5C55] mb-1">Test (simulated)</div>
            <div className="font-heading text-2xl font-black tracking-tighter text-[#5C5C55]">
              {fmtINR(stats.revenue_test_inr)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTable({ users, fmtDate }) {
  return (
    <div className="border border-[#E5E5E0] bg-white rounded-sm overflow-x-auto" data-testid="admin-users-table">
      <table className="w-full text-sm min-w-[900px]">
        <thead>
          <tr className="bg-[#F4F4F0] text-xs font-mono uppercase tracking-wider text-[#5C5C55]">
            <Th>User</Th>
            <Th>Joined</Th>
            <Th right>Quizzes</Th>
            <Th right>Saved</Th>
            <Th right>Progress</Th>
            <Th>Paid</Th>
            <Th>Last payment</Th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-[#5C5C55] py-12">No users yet.</td></tr>
          ) : users.map((u, i) => (
            <tr key={u.user_id} className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`admin-user-${i}`}>
              <Td>
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-8 h-8 rounded-full border border-[#E5E5E0] shrink-0" />
                  ) : (
                    <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold rounded-full shrink-0">
                      {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{u.name || "—"}</div>
                    <div className="text-xs text-[#5C5C55] font-mono truncate">{u.email}</div>
                  </div>
                </div>
              </Td>
              <Td><span className="text-xs">{fmtDate(u.created_at)}</span></Td>
              <Td right>{u.quiz_count}</Td>
              <Td right>{u.saved_count}</Td>
              <Td right>{u.progress_count}</Td>
              <Td>
                {u.paid_count > 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-sm bg-[#166534] text-white">
                    <CheckCircle2 className="w-3 h-3" /> {u.paid_count}x
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-[#5C5C55]">
                    <XCircle className="w-3 h-3" /> No
                  </span>
                )}
              </Td>
              <Td>
                {u.last_payment ? (
                  <div>
                    <div className="text-xs">{fmtDate(u.last_payment)}</div>
                    <div className="text-xs text-[#5C5C55] font-mono">
                      INR {u.last_payment_amount} · <span className={u.last_payment_mode === "live" ? "text-[#166534]" : ""}>{u.last_payment_mode || "test"}</span>
                    </div>
                  </div>
                ) : <span className="text-xs text-[#5C5C55]">—</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTable({ payments, fmtDate, fmtINR }) {
  return (
    <div className="border border-[#E5E5E0] bg-white rounded-sm overflow-x-auto" data-testid="admin-payments-table">
      <table className="w-full text-sm min-w-[900px]">
        <thead>
          <tr className="bg-[#F4F4F0] text-xs font-mono uppercase tracking-wider text-[#5C5C55]">
            <Th>When</Th>
            <Th>Customer</Th>
            <Th>Order</Th>
            <Th right>Amount</Th>
            <Th>Mode</Th>
            <Th>Status</Th>
            <Th>Method</Th>
          </tr>
        </thead>
        <tbody>
          {payments.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-[#5C5C55] py-12">No payments yet.</td></tr>
          ) : payments.map((p, i) => (
            <tr key={p.order_id || i} className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`admin-payment-${i}`}>
              <Td><span className="text-xs">{fmtDate(p.created_at)}</span></Td>
              <Td><span className="text-xs font-mono">{p.email || "—"}</span></Td>
              <Td>
                <a href={`https://dashboard.razorpay.com/app/orders/${p.order_id}`} target="_blank" rel="noopener noreferrer"
                   className="text-xs font-mono text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  {p.order_id?.slice(0, 18)}… <ExternalLink className="w-3 h-3" />
                </a>
              </Td>
              <Td right>{fmtINR(p.amount)}</Td>
              <Td>
                <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${
                  p.mode === "live" ? "bg-[#166534] text-white" : "bg-[#E5E5E0] text-[#5C5C55]"
                }`}>
                  {p.mode || "test"}
                </span>
              </Td>
              <Td>
                <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${
                  p.payment_status === "paid" ? "bg-[#166534] text-white" :
                  p.payment_status === "failed" || p.payment_status === "signature_failed" ? "bg-[#FF2A00] text-white" :
                  "bg-[#E5E5E0] text-[#5C5C55]"
                }`}>
                  {p.payment_status || "-"}
                </span>
              </Td>
              <Td><span className="text-xs">{p.payment_info?.method || "—"}</span></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Diagnostics({ data, loading, onRun }) {
  return (
    <div className="space-y-6" data-testid="admin-diagnostics">
      <div className="border border-[#E5E5E0] bg-white p-6 rounded-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-heading text-xl font-bold mb-1">Live health check</h3>
            <p className="text-sm text-[#5C5C55]">
              Verifies Razorpay reachability (both API + SDK), MongoDB, and LLM key configuration.
              Run this whenever a user reports a payment issue &mdash; you'll get an actionable error in seconds.
            </p>
          </div>
          <button
            onClick={onRun}
            disabled={loading}
            data-testid="run-diagnostics-btn"
            className="btn-primary shrink-0"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Running…</> : "Run diagnostics"}
          </button>
        </div>
      </div>

      {data && (
        <div className="border border-[#E5E5E0] bg-white p-6 rounded-sm space-y-4">
          <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-[#E5E5E0]">
            <span className={`text-xs font-mono px-2.5 py-1 rounded-sm ${
              data.overall_ok ? "bg-[#166534] text-white" : "bg-[#FF2A00] text-white"
            }`}>
              {data.overall_ok ? "ALL SYSTEMS GO" : "ISSUES DETECTED"}
            </span>
            <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${
              data.mode === "live" ? "bg-[#166534] text-white" : "bg-[#E5E5E0] text-[#5C5C55]"
            }`}>
              mode: {data.mode}
            </span>
            <span className="text-xs text-[#5C5C55] ml-auto">checked {new Date(data.checked_at).toLocaleString()}</span>
          </div>

          {/* Host trace */}
          <div className="bg-[#F4F4F0] border border-[#E5E5E0] rounded-sm p-4 text-xs font-mono space-y-1.5">
            <div><span className="text-[#5C5C55]">raw Host:</span> {data.host || "(none)"}</div>
            <div><span className="text-[#5C5C55]">X-Forwarded-Host:</span> {data.x_forwarded_host || "(none)"}</div>
            <div>
              <span className="text-[#5C5C55]">detected public hosts:</span>{" "}
              <span className="text-[#002FA7]">{(data.detected_public_hosts || []).join(", ") || "(none)"}</span>
            </div>
            <div><span className="text-[#5C5C55]">production hosts allowlist:</span> {(data.production_hosts_configured || []).join(", ")}</div>
            {data.payments_mode_override && (
              <div><span className="text-[#5C5C55]">PAYMENTS_MODE override:</span> <b>{data.payments_mode_override}</b></div>
            )}
          </div>

          <div className="space-y-2">
            {Object.entries(data.checks).map(([name, val]) => {
              const ok = val.ok !== false;
              return (
                <div key={name} className="flex items-start gap-3 py-2 border-b border-[#E5E5E0] last:border-0">
                  <span className={`inline-block w-2 h-2 rounded-full mt-2 shrink-0 ${ok ? "bg-[#166534]" : "bg-[#FF2A00]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-semibold">{name}</div>
                    <div className="text-xs text-[#5C5C55] font-mono break-all">
                      {Object.entries(val)
                        .filter(([k]) => k !== "ok")
                        .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
                        .join(" · ")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 font-mono font-bold text-[10px] ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right }) => (
  <td className={`px-4 py-3 align-middle ${right ? "text-right" : ""}`}>{children}</td>
);
